import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { extension, command, keyBinding, NodeExtension, findUploadPlaceholderPayload, ExtensionTag, ExtensionPriority, omitExtraAttributes, getTextSelection, uploadFile } from '@remirror/core';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { useState, useEffect } from 'react';
import { useCommands } from '@remirror/react';
import { File2LineIcon, CloseLineIcon, DeleteBin6LineIcon, DownloadLineIcon } from '@remirror/react-components/all-icons';
import { ExtensionFileTheme } from '@remirror/theme';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import _asyncToGenerator from '@babel/runtime/helpers/esm/asyncToGenerator';
import _regeneratorRuntime from '@babel/runtime/regenerator';

var FileComponent = _ref => {
  var _attrs$fileSize;

  var node = _ref.node,
      getPosition = _ref.getPosition,
      context = _ref.context,
      abort = _ref.abort;
  var attrs = node.attrs;
  var fileSize = formatFileSize((_attrs$fileSize = attrs.fileSize) !== null && _attrs$fileSize !== void 0 ? _attrs$fileSize : 0);
  return /*#__PURE__*/jsxs("div", {
    className: ExtensionFileTheme.FILE_ROOT,
    children: [/*#__PURE__*/jsx(IconButton, {
      children: /*#__PURE__*/jsx(File2LineIcon, {})
    }), /*#__PURE__*/jsx("div", {
      className: ExtensionFileTheme.FILE_NAME,
      children: attrs.fileName
    }), /*#__PURE__*/jsx("div", {
      className: ExtensionFileTheme.FILE_SIZE,
      children: fileSize
    }), /*#__PURE__*/jsx("div", {
      style: {
        flex: 1,
        minWidth: '8px'
      }
    }), context ? /*#__PURE__*/jsx(UploadingFileAction, {
      context: context,
      abort: abort
    }) : /*#__PURE__*/jsx(UploadedFileAction, {
      attrs: attrs,
      getPosition: getPosition
    })]
  });
};

var UploadingFileAction = _ref2 => {
  var context = _ref2.context,
      abort = _ref2.abort;
  var progress = useProgress(context);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: ExtensionFileTheme.FILE_UPLOAD_PROGRESS,
      children: progress
    }), /*#__PURE__*/jsx(StopButton, {
      abort: abort
    })]
  });
};

var UploadedFileAction = _ref3 => {
  var attrs = _ref3.attrs,
      getPosition = _ref3.getPosition;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [attrs.error ? /*#__PURE__*/jsx("div", {
      className: ExtensionFileTheme.FILE_ERROR,
      children: attrs.error
    }) : /*#__PURE__*/jsx(DownloadFileButton, {
      attrs: attrs
    }), /*#__PURE__*/jsx("div", {
      style: {
        minWidth: '8px'
      }
    }), /*#__PURE__*/jsx(DeleteFileButton, {
      getPosition: getPosition
    })]
  });
};

var IconButton = props => {
  return /*#__PURE__*/jsx("a", _objectSpread(_objectSpread({}, props), {}, {
    className: ExtensionFileTheme.FILE_ICON_BUTTON,
    style: {
      cursor: props.onClick || props.href ? 'pointer' : 'default'
    },
    children: props.children
  }));
};

var StopButton = _ref4 => {
  var abort = _ref4.abort;
  return /*#__PURE__*/jsx(IconButton, {
    onClick: abort,
    children: /*#__PURE__*/jsx(CloseLineIcon, {})
  });
};

var DeleteFileButton = _ref5 => {
  var getPosition = _ref5.getPosition;

  var _useCommands = useCommands(),
      deleteFile = _useCommands.deleteFile;

  return /*#__PURE__*/jsx(IconButton, {
    onClick: () => deleteFile(getPosition()),
    children: /*#__PURE__*/jsx(DeleteBin6LineIcon, {})
  });
};

var DownloadFileButton = _ref6 => {
  var attrs = _ref6.attrs;
  return /*#__PURE__*/jsx(IconButton, {
    href: attrs.url,
    download: attrs.fileName,
    target: "_blank",
    rel: "noreferrer",
    children: /*#__PURE__*/jsx(DownloadLineIcon, {})
  });
}; // Taken from: https://gist.github.com/zentala/1e6f72438796d74531803cc3833c039c


function formatFileSize(bytes) {
  var decimals = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 2;

  if (bytes === 0) {
    return '0';
  }

  var k = 1024,
      sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));
  return "".concat(Number.parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)), " ").concat(sizes[i]);
} // Returns a number between 0 and 100


function formatProgress(contextValues) {
  var loaded = contextValues['loaded'];
  var total = contextValues['total'];

  if (typeof loaded !== 'number' || loaded < 0) {
    loaded = 0;
  }

  if (typeof total !== 'number' || total <= 0) {
    total = 1;
  }

  var progress = loaded / total * 100;
  return Math.min(progress, 100);
}

function useProgress(context) {
  // progress is a number between 0 and 100
  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      progress = _useState2[0],
      setProgress = _useState2[1];

  useEffect(() => {
    context.addListener(values => {
      setProgress(formatProgress(values));
    });
  }, [context]);
  return "".concat(progress.toFixed(1), "%");
}

class DataUrlFileUploader {
  constructor() {
    _defineProperty(this, "file", null);

    this.fileReader = new FileReader();
  }

  insert(file) {
    this.file = file;
    return this.getBaseAttrs();
  }

  upload() {
    var fileReader = this.fileReader;
    var file = this.file;

    if (!file) {
      throw new Error('No file to upload');
    }

    fileReader.readAsDataURL(file);
    return new Promise((resolve, reject) => {
      fileReader.addEventListener('load', () => resolve(_objectSpread(_objectSpread({}, this.getBaseAttrs()), {}, {
        url: fileReader.result
      })));
      fileReader.addEventListener('error', () => {
        reject(fileReader.error);
      });
    });
  }

  abort() {
    this.fileReader.abort();
  }

  getBaseAttrs() {
    if (!this.file) {
      throw new Error('No file to upload');
    }

    return getDefaultFileAttrs(this.file);
  }

}
function createDataUrlFileUploader() {
  return new DataUrlFileUploader();
}
function getDefaultFileAttrs(file) {
  return {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size
  };
}

class BaseuploadFileUploader {
  constructor() {
    _defineProperty(this, "file", null);

    this.xhr = new XMLHttpRequest();
    this.uploadPromise = new Promise((resolve, reject) => {
      this.xhr.addEventListener('load', () => {
        try {
          var _JSON$parse, _JSON$parse$file;

          var response = this.xhr.responseText;
          var rawUrl = (_JSON$parse = JSON.parse(response)) === null || _JSON$parse === void 0 ? void 0 : (_JSON$parse$file = _JSON$parse.file) === null || _JSON$parse$file === void 0 ? void 0 : _JSON$parse$file.url;

          if (!rawUrl) {
            throw new Error('incorrect response structure');
          }

          resolve("".concat(rawUrl, "?download=1"));
        } catch (error) {
          reject(new Error("something went wrong when parsing the server response: ".concat(error)));
        }
      });
      this.xhr.addEventListener('error', () => {
        reject(new Error("something went wrong when uploading the file"));
      });
      this.xhr.addEventListener('abort', () => {
        reject(new Error("Aborted"));
      });
    });
  }

  insert(file) {
    this.file = file;
    return getDefaultFileAttrs(file);
  }

  upload(context) {
    var _this = this;

    return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var file, form, uploadUrl;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              file = _this.file;

              if (file) {
                _context.next = 3;
                break;
              }

              throw new Error('No file to upload');

            case 3:
              _this.xhr.addEventListener('progress', event => {
                context.set('loaded', event.loaded);
                context.set('total', event.total);
              }); // start reading and uploading


              _this.xhr.open('POST', 'https://bashupload.com/upload');

              form = new FormData();
              form.append('file', file, file.name);
              form.append('json', 'true');

              _this.xhr.send(form); // wait for the upload to finish


              _context.next = 11;
              return _this.uploadPromise;

            case 11:
              uploadUrl = _context.sent;
              return _context.abrupt("return", _objectSpread(_objectSpread({}, getDefaultFileAttrs(file)), {}, {
                url: uploadUrl
              }));

            case 13:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }))();
  }

  abort() {
    this.xhr.abort();
  }

}

function createBaseuploadFileUploader() {
  return new BaseuploadFileUploader();
}

function createObjectUrlFileUploader() {
  var file;
  return {
    insert: f => {
      file = f;
      return getDefaultFileAttrs(file);
    },
    upload: () => {
      var url = URL.createObjectURL(file);
      return Promise.resolve(_objectSpread(_objectSpread({}, getDefaultFileAttrs(file)), {}, {
        url
      }));
    },
    // `URL.createObjectURL` is sychronous. There is no change to abort the loading process.
    abort: () => {}
  };
}

/**
 * Mock implementation of a FileUploader to test slow file uploads
 */

function createSlowFileUploader() {
  var file;
  var aborted = false;
  return {
    insert: f => {
      file = f;
      return getDefaultFileAttrs(file);
    },
    upload: function () {
      var _upload = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(context) {
        var total, loaded, url;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                total = 1000;
                loaded = 0;
                context.set('total', total);
                context.set('loaded', loaded);

              case 4:
                if (!(loaded < total)) {
                  _context.next = 14;
                  break;
                }

                if (!aborted) {
                  _context.next = 7;
                  break;
                }

                throw new Error('Canceled');

              case 7:
                _context.next = 9;
                return new Promise(resolve => setTimeout(resolve, 100));

              case 9:
                loaded += Math.random() * 40;
                loaded = Math.min(total, loaded);
                context.set('loaded', loaded);
                _context.next = 4;
                break;

              case 14:
                url = URL.createObjectURL(file);
                return _context.abrupt("return", _objectSpread(_objectSpread({}, getDefaultFileAttrs(file)), {}, {
                  url
                }));

              case 16:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      function upload(_x) {
        return _upload.apply(this, arguments);
      }

      return upload;
    }(),
    abort: () => {
      aborted = true;
    }
  };
}

var _excluded = ["url", "error"];

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _class, _class2;
var FileExtension = (_dec = extension({
  defaultOptions: {
    uploadFileHandler: createDataUrlFileUploader,
    render: FileComponent,
    pasteRuleRegexp: /^((?!image).)*$/i
  },
  handlerKeys: ['onDeleteFile']
}), _dec2 = command(), _dec3 = command(), _dec4 = command(), _dec5 = command(), _dec6 = command(), _dec7 = keyBinding({
  shortcut: ['Backspace', 'Delete']
}), _dec(_class = (_class2 = class FileExtension extends NodeExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "ReactComponent", props => {
      var payload = findUploadPlaceholderPayload(props.view.state, props.node.attrs.id);
      var context = payload === null || payload === void 0 ? void 0 : payload.context;

      var abort = () => payload === null || payload === void 0 ? void 0 : payload.fileUploader.abort();

      return this.options.render(_objectSpread(_objectSpread({}, props), {}, {
        context,
        abort
      }));
    });
  }

  get name() {
    return 'file';
  }

  createTags() {
    return [ExtensionTag.Block];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        id: {
          default: null
        },
        url: {
          default: ''
        },
        fileName: {
          default: ''
        },
        fileType: {
          default: ''
        },
        fileSize: {
          default: 0
        },
        error: {
          default: null
        }
      }),
      selectable: true,
      draggable: true,
      atom: true,
      content: ''
    }, override), {}, {
      parseDOM: [{
        tag: 'div[data-file]',
        priority: ExtensionPriority.Low,
        getAttrs: dom => {
          var anchor = dom;
          var url = anchor.getAttribute('data-url');
          var fileName = anchor.getAttribute('data-filename');
          var fileType = anchor.getAttribute('data-filetype');
          var fileSize = anchor.getAttribute('data-filesize');
          return _objectSpread(_objectSpread({}, extra.parse(dom)), {}, {
            url,
            fileName,
            fileType,
            fileSize
          });
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var _omitExtraAttributes = omitExtraAttributes(node.attrs, extra),
            url = _omitExtraAttributes.url,
            error = _omitExtraAttributes.error,
            rest = _objectWithoutProperties(_omitExtraAttributes, _excluded);

        var attrs = _objectSpread(_objectSpread(_objectSpread({}, extra.dom(node)), rest), {}, {
          'data-url': url,
          'data-file': '',
          'data-filename': node.attrs.fileName,
          'data-filetype': node.attrs.fileType,
          'data-filesize': node.attrs.fileSize
        });

        if (error) {
          attrs['data-error'] = error;
        }

        return ['div', attrs];
      }
    });
  }

  createPasteRules() {
    return [{
      type: 'file',
      regexp: this.options.pasteRuleRegexp,
      fileHandler: props => {
        var pos;

        if (props.type === 'drop') {
          pos = props.pos;
        }

        var _iterator = _createForOfIteratorHelper(props.files),
            _step;

        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var file = _step.value;
            this.uploadFile(file, pos);
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }

        return true;
      }
    }];
  }

  uploadFiles(files) {
    return () => {
      var _iterator2 = _createForOfIteratorHelper(files),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var file = _step2.value;
          this.uploadFile(file);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }

      return true;
    };
  }

  updateFile(pos, attrs) {
    return _ref => {
      var tr = _ref.tr,
          dispatch = _ref.dispatch;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(pos, undefined, attrs));
      return true;
    };
  }

  insertFile(attributes, selection) {
    return _ref2 => {
      var tr = _ref2.tr,
          dispatch = _ref2.dispatch;

      var _getTextSelection = getTextSelection(selection !== null && selection !== void 0 ? selection : tr.selection, tr.doc),
          from = _getTextSelection.from,
          to = _getTextSelection.to;

      var node = this.type.create(attributes);
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.replaceRangeWith(from, to, node));
      return true;
    };
  }

  deleteFile(pos) {
    return _ref3 => {
      var tr = _ref3.tr,
          state = _ref3.state,
          dispatch = _ref3.dispatch;
      var node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        tr.delete(pos, pos + 1).scrollIntoView();
        this.options.onDeleteFile({
          tr,
          pos,
          node
        });
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr);
        return true;
      }

      return false;
    };
  }

  renameFile(pos, fileName) {
    return _ref4 => {
      var tr = _ref4.tr,
          state = _ref4.state,
          dispatch = _ref4.dispatch;
      var node = state.doc.nodeAt(pos);

      if (node && node.type === this.type) {
        dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(pos, undefined, _objectSpread(_objectSpread({}, node.attrs), {}, {
          fileName
        })));
        return true;
      }

      return false;
    };
  }

  backspaceShortcut(props) {
    var tr = props.tr,
        state = props.state;
    var _tr$selection = tr.selection,
        from = _tr$selection.from,
        to = _tr$selection.to,
        empty = _tr$selection.empty;

    if (!this.hasHandlers('onDeleteFile') || empty) {
      return false;
    } // Collect a list of files nodes contained within this delete range


    var onDeleteFileCallbacks = [];
    state.doc.nodesBetween(from, to, (node, pos) => {
      if (node.type === this.type) {
        onDeleteFileCallbacks.push({
          node,
          pos
        });
      }

      return true;
    }); // Call the onDeleteFile callback for each file being deleted.

    onDeleteFileCallbacks.forEach(_ref5 => {
      var node = _ref5.node,
          pos = _ref5.pos;
      this.options.onDeleteFile({
        tr,
        node,
        pos
      });
    }); // Don't need to handle the delete ourselves, just the callbacks

    return false;
  }

  uploadFile(file, pos) {
    return uploadFile({
      file,
      pos,
      view: this.store.view,
      fileType: this.type,
      uploadHandler: this.options.uploadFileHandler
    });
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "uploadFiles", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "uploadFiles"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "updateFile", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "updateFile"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "insertFile", [_dec4], Object.getOwnPropertyDescriptor(_class2.prototype, "insertFile"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "deleteFile", [_dec5], Object.getOwnPropertyDescriptor(_class2.prototype, "deleteFile"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "renameFile", [_dec6], Object.getOwnPropertyDescriptor(_class2.prototype, "renameFile"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "backspaceShortcut", [_dec7], Object.getOwnPropertyDescriptor(_class2.prototype, "backspaceShortcut"), _class2.prototype)), _class2)) || _class);

export { DataUrlFileUploader, FileExtension, createBaseuploadFileUploader, createDataUrlFileUploader, createObjectUrlFileUploader, createSlowFileUploader, getDefaultFileAttrs };
